import { lazy } from 'react';

import LegacyRouteResolver from '@/components/layout/LegacyRouteResolver';
import MainLayout from '@/components/layout/MainLayout';
import { AuthGuard } from '@/components/routing/AuthGuard';
import { HomeRedirect } from '@/components/routing/HomeRedirect';
import { RoleGuard } from '@/components/routing/RoleGuard';
import { ErrorFallback } from '@/components/ui/ErrorFallback';
import { NotFoundScreen } from '@/components/ui/NotFoundScreen';
import { UnauthorizedScreen } from '@/components/ui/UnauthorizedScreen';
import { ROLES } from '@/consts/roles';
import { ROUTES } from '@/consts/routes';
import { env } from '@/env/env';
import { AcceptInvitationScreen } from '@/features/auth/accept-invitation';
import { LoginScreen } from '@/features/auth/login';
import { PasswordResetScreen } from '@/features/auth/password-reset';
import { SsoCallbackScreen } from '@/features/auth/sso-callback';
import { setTelemetryRoute } from '@/lib/telemetry';
import { deepestRouteId } from '@/routing/deepestRouteId';
import { createBrowserRouter, Navigate } from 'react-router';

const DefineComponentLibraryLayout = lazy(
  () => import('@/modules/PMS/features/define-component-library/components/DefineComponentLibraryLayout')
);
const ComponentsView = lazy(() => import('@/modules/PMS/views/DefineComponentLibrary/ComponentsView'));
const SparesView = lazy(() => import('@/modules/PMS/views/DefineComponentLibrary/SparesView'));
const JobsView = lazy(() => import('@/modules/PMS/views/DefineComponentLibrary/JobsView'));
const VesselComponentLibraryLayout = lazy(
  () => import('@/modules/PMS/features/vessel-components/components/VesselComponentLibraryLayout')
);
const VesselComponentsView = lazy(() => import('@/modules/PMS/views/VesselComponentLibrary/VesselComponentsView'));
const VesselJobsView = lazy(() => import('@/modules/PMS/views/VesselComponentLibrary/VesselJobsView'));
const VesselAssignView = lazy(() => import('@/modules/PMS/views/VesselAssign/VesselAssignView'));
const MainLibrariesView = lazy(() => import('@/modules/PMS/views/MainLibraries/MainLibrariesView'));
const FleetJobsView = lazy(() => import('@/modules/PMS/views/FleetJobs/FleetJobsView'));
const FleetJobDetailView = lazy(() => import('@/modules/PMS/views/FleetJobs/FleetJobDetailView'));
const RunningHoursView = lazy(() => import('@/modules/PMS/views/RunningHours/RunningHoursView'));
const ApprovalsView = lazy(() => import('@/modules/PMS/views/Approvals/ApprovalsView'));
const WorkflowsPage = lazy(() =>
  import('@/modules/PMS/features/workflows/components/WorkflowsPage').then((m) => ({ default: m.WorkflowsPage }))
);
const WorkflowDetailPage = lazy(() =>
  import('@/modules/PMS/features/workflows/components/WorkflowDetailPage').then((m) => ({
    default: m.WorkflowDetailPage
  }))
);
const WorkflowBuilderPage = lazy(() =>
  import('@/modules/PMS/features/workflows/components/WorkflowBuilderPage').then((m) => ({
    default: m.WorkflowBuilderPage
  }))
);
const AbstractComponentEditPage = lazy(
  () => import('@/modules/PMS/views/DefineComponentLibrary/AbstractComponentEditPage')
);
const SpareEditPage = lazy(() => import('@/modules/PMS/views/DefineComponentLibrary/SpareEditPage'));
const JobEditPage = lazy(() => import('@/modules/PMS/views/DefineComponentLibrary/JobEditPage'));
const VesselComponentEditPage = lazy(
  () => import('@/modules/PMS/views/VesselComponentLibrary/VesselComponentEditPage')
);
const VesselSpareEditPage = lazy(() => import('@/modules/PMS/views/VesselComponentLibrary/VesselSpareEditPage'));
const VesselJobEditPage = lazy(() => import('@/modules/PMS/views/VesselComponentLibrary/VesselJobEditPage'));
const OverviewLayout = lazy(() => import('@/modules/PUR/features/overview/components/OverviewLayout'));
const RequisitionsView = lazy(() => import('@/modules/PUR/views/Overview/RequisitionsView'));
const CreateRequisitionView = lazy(() => import('@/modules/PUR/views/Requisition/CreateRequisitionView'));
const RequisitionPreviewView = lazy(() => import('@/modules/PUR/views/Requisition/RequisitionPreviewView'));
// The RFQGroup detail page; a sibling of RequisitionPreviewView, not a replacement for the
// RfqView list tab below (that stays a stub, owned by another ticket).
const RfqGroupPreviewView = lazy(() => import('@/modules/PUR/views/Rfq/RfqGroupPreviewView'));
// CHR-1135 — the real RFQ round detail page (`/pur/rfq/:id`). Also keyed on an RFQGroups.Id; see the
// note in `consts/routes.ts` for why the path segment no longer tells the two id kinds apart.
const RfqDetailView = lazy(() => import('@/modules/PUR/views/Rfq/RfqDetailView'));
const RfqView = lazy(() => import('@/modules/PUR/views/Overview/RfqView'));
const PurchaseOrdersView = lazy(() => import('@/modules/PUR/views/Overview/PurchaseOrdersView'));
const VendorsView = lazy(() => import('@/modules/PUR/views/Vendors/VendorsView'));
const VendorRequestView = lazy(() => import('@/modules/PUR/views/Vendors/VendorRequestView'));
const VendorDetailView = lazy(() => import('@/modules/PUR/views/Vendors/VendorDetailView'));
const CatalogueLayout = lazy(() => import('@/modules/PUR/features/catalogue/components/CatalogueLayout'));
const CatalogueRouteView = lazy(() => import('@/modules/PUR/features/catalogue/components/CatalogueRouteView'));
const CatalogueItemDetailView = lazy(
  () => import('@/modules/PUR/features/catalogue/components/CatalogueItemDetailView')
);
const PaymentsLayout = lazy(() => import('@/modules/ACC/components/PaymentsLayout'));
const PaymentsView = lazy(() => import('@/modules/ACC/views/PaymentsView'));
const TimeCharterInDashboardPage = lazy(() => import('@/modules/VOY/views/TimeCharterIn/TimeCharterInDashboardPage'));
const TimeCharterInDetailPage = lazy(() => import('@/modules/VOY/views/TimeCharterIn/TimeCharterInDetailPage'));
const PaymentOrdersView = lazy(() => import('@/modules/ACC/views/PaymentOrdersView'));
const PaymentsViewV2 = lazy(() => import('@/modules/ACC/views/PaymentsViewV2'));
const ApiDocsPage = lazy(() => import('@/features/api-docs/views/ApiDocsPage'));
const EstimatorPage = lazy(() => import('@/modules/VOY/views/EstimatorPage'));
const HybridCalcPage = lazy(() => import('@/modules/VOY/views/HybridCalcPage'));
const MarketIndexDashboard = lazy(() => import('@/modules/VOY/pages/MarketIndexDashboard'));
const ContractsPage = lazy(() => import('@/modules/VOY/views/ContractsPage'));
const TenantSettingsPage = lazy(() => import('@/features/tenant-settings/components/TenantSettingsPage'));
const MenuEditorPage = lazy(() => import('@/features/menu-editor/components/MenuEditorPage'));
const BunkerPriceLastView = lazy(() => import('@/modules/BUN/views/BunkerPriceLastView'));
// HSQE views — per-view lazy imports so Rollup emits a separate chunk per route (F16).
// HSQE "My Work" — personal tasks + oversight hub.
const SmsTasksPage = lazy(() => import('@/modules/HSQE/views/Tasks/SmsTasksPage'));
const SmsLibraryDocumentPage = lazy(() => import('@/modules/HSQE/views/Library/SmsLibraryDocumentPage'));
// SMS Library two-pane folder browser (HSQE-60, production view).
const SmsLibraryBrowsePage = lazy(() =>
  import('@/modules/HSQE/views/Library/SmsLibraryBrowse').then((m) => ({ default: m.SmsLibraryBrowse }))
);
// SMS UI-Customization admin screen (Settings → UI Customization, production view).
const SmsCustomFieldsConfigPage = lazy(() =>
  import('@/modules/HSQE/views/Settings/UiCustomization/CustomFieldsConfigPage').then((m) => ({
    default: m.CustomFieldsConfigPage
  }))
);
// SMS Workflows & Approvals registry screen (Settings → Workflows, production view).
const SmsWorkflowsSettingsPage = lazy(() =>
  import('@/modules/HSQE/views/Settings/Workflows/WorkflowsSettingsPage').then((m) => ({
    default: m.WorkflowsSettingsPage
  }))
);
const SmsDocumentNewPage = lazy(() => import('@/modules/HSQE/views/Library/SmsDocumentNewPage'));
// PROTOTYPE (hsqe-sms-review) — Master's SMS Review list (E5).
const SmsMastersReviewListPage = lazy(() =>
  import('@/modules/HSQE/views').then((m) => ({ default: m.SmsMastersReviewListPage }))
);
// PROTOTYPE (hsqe-sms-review) — Master's SMS Review detail (E5).
const SmsMastersReviewDetailPage = lazy(() =>
  import('@/modules/HSQE/views').then((m) => ({ default: m.SmsMastersReviewDetailPage }))
);
// PROTOTYPE (hsqe-sms-library) — SMS "Edit document metadata" form.
const SmsDocumentEditPage = lazy(() => import('@/modules/HSQE/views/Library/SmsDocumentEditPage'));
// PROTOTYPE (hsqe-sms-versioning, E3) — versioning pages.
const SmsChangeRequestsListPage = lazy(
  () => import('@/modules/HSQE/views/Library/ChangeRequestsList/SmsChangeRequestsListPage')
);
const SmsChangeRequestNewPage = lazy(
  () => import('@/modules/HSQE/views/Library/ChangeRequestNew/SmsChangeRequestNewPage')
);
const SmsVersionComparePage = lazy(() => import('@/modules/HSQE/views/Library/VersionCompare/SmsVersionComparePage'));
const SmsSectionEditorPage = lazy(() => import('@/modules/HSQE/views/Library/SectionEditor/SmsSectionEditorPage'));
const EnergyPerformancePage = lazy(() => import('@/features/energy-performance/components/EnergyPerformancePage'));

const baseName = env.VITE_MOCK_MODE === 'true' ? '/' : env.VITE_BASE_URL;

export const router = createBrowserRouter(
  [
    { path: ROUTES.auth.login, element: <LoginScreen /> },
    { path: ROUTES.user.passwordReset, element: <PasswordResetScreen /> },
    { path: ROUTES.auth.sso, element: <SsoCallbackScreen /> },
    { path: ROUTES.auth.unauthorized, element: <UnauthorizedScreen /> },
    { path: ROUTES.auth.signup, element: <AcceptInvitationScreen /> },
    { path: ROUTES.auth.acceptInvitation, element: <AcceptInvitationScreen /> },
    {
      path: '/',
      element: (
        <AuthGuard>
          <MainLayout />
        </AuthGuard>
      ),
      errorElement: <ErrorFallback />,
      children: [
        { index: true, element: <HomeRedirect /> },
        { id: 'estimator', path: ROUTES.chartering.estimator.replace('/', ''), element: <EstimatorPage /> },
        { id: 'estimator-new', path: ROUTES.chartering.estimatorNew.replace('/', ''), element: <EstimatorPage /> },
        { id: 'hybrid-calc', path: ROUTES.chartering.hybridCalc.replace('/', ''), element: <HybridCalcPage /> },
        { path: ROUTES.chartering.marketIndices.replace('/', ''), element: <MarketIndexDashboard /> },
        {
          path: ROUTES.chartering.contracts.replace('/', ''),
          element: <ContractsPage />,
          loader: () => ({ crumb: 'Contracts' })
        },
        { path: ROUTES.bun.bunkerPriceLast.replace('/', ''), element: <BunkerPriceLastView /> },
        { path: ROUTES.bun.bunkerPriceLog.replace('/', ''), element: <BunkerPriceLastView /> },
        // PMS routes + API docs - all four PMS roles reach the subtree; per-view booleans control which actions each role sees.
        {
          element: (
            <RoleGuard
              requiredRoles={[
                ROLES.PMS_ADMIN,
                ROLES.PMS_ADMIN_TECHNICAL,
                ROLES.PMS_CHIEF_ENGINEER,
                ROLES.PMS_VESSEL_CREW_RESPONSIBLE
              ]}
            />
          ),
          children: [
            // API Documentation routes
            {
              path: 'docs/api',
              element: <ApiDocsPage />,
              loader: () => ({ crumb: 'API Documentation' })
            },
            {
              path: 'docs/api/:module',
              element: <ApiDocsPage />,
              loader: () => ({ crumb: 'API Documentation' })
            },
            {
              path: 'docs/api/:module/:entity',
              element: <ApiDocsPage />,
              loader: () => ({ crumb: 'API Documentation' })
            },
            {
              // Component Library (abstract definitions): Crew has no access per the role matrix; Tech edits, Chief views.
              element: (
                <RoleGuard requiredRoles={[ROLES.PMS_ADMIN, ROLES.PMS_ADMIN_TECHNICAL, ROLES.PMS_CHIEF_ENGINEER]} />
              ),
              children: [
                {
                  path: ROUTES.pms.defineComponentLibrary.replace('/', ''),
                  element: <DefineComponentLibraryLayout />,
                  loader: () => ({ crumb: 'Component Library' }),
                  children: [
                    { index: true, element: <ComponentsView />, loader: () => ({ crumb: 'Components' }) },
                    { path: 'components', element: <ComponentsView />, loader: () => ({ crumb: 'Components' }) },
                    { path: 'spares', element: <SparesView />, loader: () => ({ crumb: 'Spares' }) },
                    { path: 'jobs', element: <JobsView />, loader: () => ({ crumb: 'Jobs' }) }
                  ]
                },
                {
                  path: 'pms/define-component-library/components/new',
                  element: <AbstractComponentEditPage />,
                  loader: () => ({ crumb: 'New Component' })
                },
                {
                  path: 'pms/define-component-library/components/:id/edit',
                  element: <AbstractComponentEditPage />,
                  loader: () => ({ crumb: 'Edit Component' })
                },
                // Spare edit pages - rendered outside DefineComponentLibraryLayout to hide top tabs
                {
                  path: 'pms/define-component-library/spares/new',
                  element: <SpareEditPage />,
                  loader: () => ({ crumb: 'New Spare' })
                },
                {
                  path: 'pms/define-component-library/spares/:id/edit',
                  element: <SpareEditPage />,
                  loader: () => ({ crumb: 'Edit Spare' })
                },
                // Job edit pages - rendered outside DefineComponentLibraryLayout to hide top tabs
                {
                  path: 'pms/define-component-library/jobs/new',
                  element: <JobEditPage />,
                  loader: () => ({ crumb: 'New Job' })
                },
                {
                  path: 'pms/define-component-library/jobs/:id/edit',
                  element: <JobEditPage />,
                  loader: () => ({ crumb: 'Edit Job' })
                }
              ]
            },
            {
              path: ROUTES.pms.vesselComponentLibrary.replace('/', ''),
              element: <VesselComponentLibraryLayout />,
              loader: () => ({ crumb: 'Vessel Library' }),
              children: [
                { index: true },
                {
                  path: ':vesselId',
                  children: [
                    { index: true, element: <VesselComponentsView />, loader: () => ({ crumb: 'Components' }) },
                    { path: 'jobs', element: <VesselJobsView />, loader: () => ({ crumb: 'Jobs' }) }
                  ]
                }
              ]
            },
            {
              path: 'pms/vessels/:vesselId/assign',
              element: <VesselAssignView />,
              loader: () => ({ crumb: 'Assign Components' })
            },
            // Vessel Component edit pages
            {
              path: 'pms/vessels/:vesselId/components/new',
              element: <VesselComponentEditPage />,
              loader: () => ({ crumb: 'New Component' })
            },
            {
              path: 'pms/vessels/:vesselId/components/:componentId/edit',
              element: <VesselComponentEditPage />,
              loader: () => ({ crumb: 'Edit Component' })
            },
            // Vessel Spare edit pages
            {
              path: 'pms/vessels/:vesselId/spares/new',
              element: <VesselSpareEditPage />,
              loader: () => ({ crumb: 'New Spare' })
            },
            {
              path: 'pms/vessels/:vesselId/spares/:spareId/edit',
              element: <VesselSpareEditPage />,
              loader: () => ({ crumb: 'Edit Spare' })
            },
            // Vessel Job edit pages
            {
              path: 'pms/vessels/:vesselId/jobs/new',
              element: <VesselJobEditPage />,
              loader: () => ({ crumb: 'New Job' })
            },
            {
              path: 'pms/vessels/:vesselId/jobs/:jobId/edit',
              element: <VesselJobEditPage />,
              loader: () => ({ crumb: 'Edit Job' })
            },
            {
              element: <RoleGuard requiredRoles={[ROLES.PMS_ADMIN_TECHNICAL]} />,
              children: [
                {
                  path: ROUTES.pms.mainLibraries.replace('/', ''),
                  element: <MainLibrariesView />,
                  loader: () => ({ crumb: 'Main Libraries' })
                }
              ]
            },
            {
              path: ROUTES.pms.fleetJobDetail.replace('/', ''),
              element: <FleetJobDetailView />,
              loader: () => ({ crumb: 'Fleet Job' })
            },
            {
              path: ROUTES.pms.fleetJobDetail.replace('/', ''),
              element: <FleetJobDetailView />,
              loader: () => ({ crumb: 'Fleet Job' })
            },
            {
              path: ROUTES.pms.fleetJobs.replace('/', ''),
              element: <FleetJobsView />,
              loader: () => ({ crumb: 'Fleet Jobs' })
            },
            {
              path: ROUTES.pms.runningHours.replace('/', ''),
              element: <RunningHoursView />,
              loader: () => ({ crumb: 'Running Hours' }),
              children: [{ index: true }, { path: ':vesselId' }]
            },
            {
              // Approvals reviewer dashboard: office side only (Admin + Technical Superintendent) per the role matrix.
              element: <RoleGuard requiredRoles={[ROLES.PMS_ADMIN, ROLES.PMS_ADMIN_TECHNICAL]} />,
              children: [
                {
                  path: ROUTES.pms.approvals.replace('/', ''),
                  element: <ApprovalsView />,
                  loader: () => ({ crumb: 'Approvals' })
                }
              ]
            },

            {
              path: ROUTES.administration.workflowDefinitions.replace('/', ''),
              element: <WorkflowsPage />,
              loader: () => ({ crumb: 'Workflows' })
            },
            {
              path: ROUTES.administration.workflowDefinitionNew.replace('/', ''),
              element: <WorkflowBuilderPage />,
              loader: () => ({ crumb: 'Workflow Builder' })
            },
            {
              path: ROUTES.administration.workflowDefinitionEdit.replace('/', ''),
              element: <WorkflowBuilderPage />,
              loader: () => ({ crumb: 'Edit Workflow' })
            },
            {
              path: ROUTES.administration.workflowDefinitionDetail.replace('/', ''),
              element: <WorkflowDetailPage />,
              loader: () => ({ crumb: 'Workflow' })
            }
          ]
        },
        // PUR routes - protected by PUR.ADMIN role
        {
          element: <RoleGuard requiredRoles={[ROLES.PUR_ADMIN]} />,
          children: [
            {
              path: ROUTES.pur.overview.replace('/', ''),
              element: <OverviewLayout />,
              loader: () => ({ crumb: 'Overview' }),
              children: [
                { index: true, element: <RequisitionsView />, loader: () => ({ crumb: 'Requisitions' }) },
                { path: 'requisitions', element: <RequisitionsView />, loader: () => ({ crumb: 'Requisitions' }) },
                { path: 'rfq', element: <RfqView />, loader: () => ({ crumb: 'RFQ' }) },
                {
                  path: 'purchase-orders',
                  element: <PurchaseOrdersView />,
                  loader: () => ({ crumb: 'Purchase Orders' })
                }
              ]
            },
            {
              path: ROUTES.pur.requisitionNew.replace('/', ''),
              element: <CreateRequisitionView />,
              loader: () => ({ crumb: 'New Requisition' })
            },
            {
              path: 'pur/requisition/:id',
              element: <RequisitionPreviewView />,
              loader: () => ({ crumb: 'Requisition Details' })
            },
            {
              // An RFQGroups.Id, not an RFQs.Id (see `routes.ts`). The provisional preview;
              // `pur/overview/rfq` above stays the list-tab stub.
              path: 'pur/rfq-group/:id',
              element: <RfqGroupPreviewView />,
              loader: () => ({ crumb: 'RFQ Details' })
            },
            {
              // The RFQ round detail page. Also an RFQGroups.Id — the same id kind the
              // `rfq-group` route above takes; `routes.ts` records why that is no longer
              // readable off the path.
              path: ROUTES.pur.rfqDetail.replace('/', ''),
              element: <RfqDetailView />,
              loader: () => ({ crumb: 'RFQ Detail' })
            },
            {
              path: ROUTES.pur.catalogue.replace('/', ''),
              element: <CatalogueLayout />,
              loader: () => ({ crumb: 'Catalogue' }),
              children: [
                { index: true, element: <CatalogueRouteView /> },
                { path: ':catalogueId', element: <CatalogueRouteView /> }
              ]
            },
            {
              // Full-page item editor (CHR-976) — sibling of the catalogue layout so it renders without
              // the tab strip, matching the prototype's "Catalogue Detail" page and the Vendor Detail pattern.
              path: 'pur/catalogue/:catalogueId/item/new',
              element: <CatalogueItemDetailView />,
              loader: () => ({ crumb: 'New Item' })
            },
            {
              path: 'pur/catalogue/:catalogueId/item/:itemId',
              element: <CatalogueItemDetailView />,
              loader: () => ({ crumb: 'Item Detail' })
            },
            {
              path: ROUTES.pur.vendors.replace('/', ''),
              loader: () => ({ crumb: 'Vendors' }),
              children: [
                {
                  index: true,
                  element: <VendorsView />,
                  loader: () => ({ crumb: 'Vendors' })
                },
                {
                  // Ordered before ':id' so "request" is not captured as a vendor id.
                  path: 'request',
                  element: <VendorRequestView />,
                  loader: () => ({ crumb: 'Request New Vendor' })
                },
                {
                  path: ':id',
                  element: <VendorDetailView />,
                  loader: () => ({ crumb: 'Vendor Detail' })
                }
              ]
            }
          ]
        },
        {
          element: <RoleGuard requiredRoles={[ROLES.ACC_JUNIOR, ROLES.ACC_SENIOR, ROLES.ACC_VIEW_V1]} />,
          children: [
            {
              path: ROUTES.acc.payments.replace('/', ''),
              element: <PaymentsLayout />,
              loader: () => ({ crumb: 'Payments v1' }),
              children: [
                {
                  id: 'acc-payments-v1',
                  index: true,
                  element: <PaymentsView />,
                  loader: () => ({ crumb: 'Payment Instructions' })
                },
                {
                  id: 'acc-payments-v1-orders',
                  path: 'orders',
                  element: <PaymentOrdersView />,
                  loader: () => ({ crumb: 'Payment Orders' })
                }
              ]
            }
          ]
        },
        {
          element: <RoleGuard requiredRoles={[ROLES.ACC_VIEW_V2]} />,
          children: [
            {
              id: 'acc-payments-v2',
              path: ROUTES.acc.paymentsV2.replace('/', ''),
              element: <PaymentsViewV2 />,
              loader: () => ({ crumb: 'Payments' })
            }
          ]
        },

        // VOY routes
        {
          element: <RoleGuard requiredRoles={[ROLES.TCIN_VIEWER]} />,
          children: [
            {
              path: ROUTES.operations.tcin.replace('/', ''),
              element: <TimeCharterInDashboardPage />,
              loader: () => ({ crumb: 'TC-IN' })
            },
            {
              // Handles BOTH /operations/tc-in/new (id === 'new') and a real id — one component,
              // one route, so a create -> real-id transition after saving never remounts (see
              // TimeCharterInDetailPage's own comment on `createdEntry`).
              path: ROUTES.operations.tcinDetail.replace('/', ''),
              element: <TimeCharterInDetailPage />,
              loader: () => ({ crumb: 'TC-IN' })
            }
          ]
        },

        // Admin routes - protected by ADM role
        {
          element: <RoleGuard requiredRoles={[ROLES.ADM]} />,
          children: [
            {
              path: ROUTES.admin.tenantSettings.replace('/', ''),
              element: <TenantSettingsPage />,
              loader: () => ({ crumb: 'Tenant Settings' })
            },
            {
              path: ROUTES.admin.menuEditor.replace('/', ''),
              element: <MenuEditorPage />,
              loader: () => ({ crumb: 'Menu Editor' })
            }
          ]
        },
        // HSQE routes - protected by HSQE-S role (ADR-0003). Placed BEFORE
        // LegacyRouteResolver so /hsqe never falls through to legacy resolution.
        {
          element: <RoleGuard requiredRoles={[ROLES.HSQE_S]} />,
          children: [
            {
              // HSQE module root redirects to the Document Library (the dashboard
              // was removed — unused). `/hsqe` → `/hsqe/library`.
              path: ROUTES.hsqe.dashboard.replace('/', ''),
              element: <Navigate to={ROUTES.hsqe.library} replace />
            },
            {
              // HSQE "My Work" — the personal/oversight task hub. Static path, declared
              // before libraryDocument (:id) so it wins the match.
              path: ROUTES.hsqe.tasks.replace('/', ''),
              element: <SmsTasksPage />,
              loader: () => ({ crumb: 'My Work' })
            },
            {
              // HSQE Library — the two-pane structure / browse view (SmsLibraryBrowse),
              // promoted to the primary Library address from /hsqe/library/browse.
              path: ROUTES.hsqe.library.replace('/', ''),
              element: <SmsLibraryBrowsePage />,
              loader: () => ({ crumb: 'Library' })
            },
            {
              // PROTOTYPE (hsqe-sms-library) — SMS "New document" create form.
              // Static path declared before libraryDocument (:id) so it wins the match.
              path: ROUTES.hsqe.libraryNew.replace('/', ''),
              element: <SmsDocumentNewPage />,
              loader: () => ({ crumb: 'New Document' })
            },
            {
              // PROTOTYPE (hsqe-sms-library) — two-pane folder browser.
              // Static path declared before libraryDocument (:id) so it wins the match.
              path: ROUTES.hsqe.libraryBrowse.replace('/', ''),
              element: <SmsLibraryBrowsePage />,
              loader: () => ({ crumb: 'Browse' })
            },
            {
              // PROTOTYPE (hsqe-sms-review) — Master's SMS Review list (E5).
              // Static path declared before libraryDocument (:id) so it wins the match.
              path: ROUTES.hsqe.mastersReviews.replace('/', ''),
              element: <SmsMastersReviewListPage />,
              loader: () => ({ crumb: "Master's Reviews" })
            },
            {
              // PROTOTYPE (hsqe-sms-review) — Master's SMS Review detail (E5).
              // Declared before libraryDocument (:id) so the nested `masters-reviews/:id`
              // path wins over the shallower `library/:id` match.
              path: ROUTES.hsqe.mastersReviewDetail.replace('/', ''),
              element: <SmsMastersReviewDetailPage />,
              loader: () => ({ crumb: 'Review' })
            },
            {
              // PROTOTYPE (hsqe-sms-versioning, E3) — fleet-wide Change Requests triage.
              // Static path declared before libraryDocument (:id) so it wins the match.
              path: ROUTES.hsqe.libraryChangeRequests.replace('/', ''),
              element: <SmsChangeRequestsListPage />,
              loader: () => ({ crumb: 'Change Requests' })
            },
            {
              // PROTOTYPE (hsqe-sms-versioning, E3) — New Change Request (doc-less entry).
              // Static path declared before libraryDocument (:id) so it wins the match.
              path: ROUTES.hsqe.libraryChangeRequestNewGlobal.replace('/', ''),
              element: <SmsChangeRequestNewPage />,
              loader: () => ({ crumb: 'New Change Request' })
            },
            {
              path: ROUTES.hsqe.libraryDocument.replace('/', ''),
              element: <SmsLibraryDocumentPage />,
              loader: () => ({ crumb: 'Document' })
            },
            {
              // PROTOTYPE (hsqe-sms-library) — SMS "Edit document metadata" form.
              path: ROUTES.hsqe.libraryDocumentEdit.replace('/', ''),
              element: <SmsDocumentEditPage />,
              loader: () => ({ crumb: 'Edit' })
            },
            {
              // PROTOTYPE (hsqe-sms-versioning, E3) — doc-scoped New Change Request.
              path: ROUTES.hsqe.libraryChangeRequestNew.replace('/', ''),
              element: <SmsChangeRequestNewPage />,
              loader: () => ({ crumb: 'New Change Request' })
            },
            {
              // PROTOTYPE (hsqe-sms-versioning, E3) — version / CR compare surface.
              path: ROUTES.hsqe.libraryVersionCompare.replace('/', ''),
              element: <SmsVersionComparePage />,
              loader: () => ({ crumb: 'Compare' })
            },
            {
              // PROTOTYPE (hsqe-sms-versioning, E3) — section autosave editor.
              path: ROUTES.hsqe.librarySectionEditor.replace('/', ''),
              element: <SmsSectionEditorPage />,
              loader: () => ({ crumb: 'Section' })
            },
            {
              // PROTOTYPE (hsqe-sms-library) — Settings → UI Customization (admin).
              path: ROUTES.hsqe.settingsUiCustomization.replace('/', ''),
              element: <SmsCustomFieldsConfigPage />,
              loader: () => ({ crumb: 'UI Customization' })
            },
            {
              // Settings → Workflows & Approvals (admin, read-only registry — HSQE-268).
              path: ROUTES.hsqe.settingsWorkflows.replace('/', ''),
              element: <SmsWorkflowsSettingsPage />,
              loader: () => ({ crumb: 'Workflows & Approvals' })
            }
          ]
        },
        {
          path: 'energy-performance/:view',
          element: <EnergyPerformancePage />,
          loader: () => ({ crumb: 'Energy Performance' })
        },
        // Catch-all: resolve legacy routes from JSON navigation config
        { path: '*', element: <LegacyRouteResolver /> }
      ]
    },
    { path: '*', element: <NotFoundScreen /> }
  ],
  { basename: baseName }
);

// The deepest match is the only level that tells the Payments v1 index and orders routes apart.
setTelemetryRoute(deepestRouteId(router.state.matches));
router.subscribe((state) => {
  setTelemetryRoute(deepestRouteId(state.matches));
});
